/**
 * useClients - TanStack Query Hooks
 *
 * Hooks pour la gestion des clients avec pagination server-side
 *
 * Pattern: Component -> Hook (TanStack Query) -> Service (API)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientsAPI, type ClientsResponse } from '@/utils/clientsAPI';
import type { Client } from '@/types/models';
import { logger } from '@/utils/logger';

// ==================== QUERY KEYS ====================

export const clientsKeys = {
  all: ['clients'] as const,
  lists: () => [...clientsKeys.all, 'list'] as const,
  list: (filters: ClientListParams) => [...clientsKeys.lists(), filters] as const,
  detail: (id: string) => [...clientsKeys.all, 'detail', id] as const,
};

// ==================== TYPES ====================

export interface ClientListParams {
  page?: number;
  limit?: number;
  search?: string;
  activeOnly?: boolean;
  clientType?: string[];
}

// ==================== QUERIES ====================

/**
 * Liste des clients avec pagination server-side
 */
export function useClients(params: ClientListParams = {}) {
  return useQuery<ClientsResponse>({
    queryKey: clientsKeys.list(params),
    queryFn: () =>
      clientsAPI.getAll({
        page: params.page || 1,
        limit: params.limit || 20,
        search: params.search || undefined,
        activeOnly: params.activeOnly,
        clientType: params.clientType,
      }),
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Détail d'un client
 */
export function useClient(clientId: string) {
  return useQuery<Client>({
    queryKey: clientsKeys.detail(clientId),
    queryFn: () => clientsAPI.getById(clientId),
    enabled: !!clientId,
  });
}

// ==================== MUTATIONS ====================

/**
 * Créer un client
 */
export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Client>) => clientsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to create client:', error);
    },
  });
}

/**
 * Mettre a jour un client
 */
export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Client> }) =>
      clientsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to update client:', error);
    },
  });
}

/**
 * Supprimer un client
 */
export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId: string) => clientsAPI.delete(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to delete client:', error);
    },
  });
}

/**
 * Toggle le statut actif/inactif d'un client
 */
export function useToggleClientStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId: string) => clientsAPI.toggleStatus(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientsKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to toggle client status:', error);
    },
  });
}
